import React, { useEffect, useRef, useState } from "react";

// Apparition en fondu au défilement, à la place de react-reveal (abandonné) :
// ses blocs restaient parfois invisibles après un changement de page ou un F5,
// faute de recalculer la visibilité. Ici :
//  - un bloc déjà à l'écran au montage s'anime tout de suite ;
//  - sinon un IntersectionObserver l'affiche dès qu'un pixel entre à l'écran,
//    quelle que soit la façon dont on est arrivé là (navigation, F5, retour) ;
//  - une fois l'animation finie, plus aucun style n'est imposé à l'élément
//    (ses propres transform / transition au survol continuent de marcher).
// Même usage que react-reveal : <Fade bottom duration={1000} distance="40px">.

const DIRECTIONS = {
  top: [0, -1],
  bottom: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function Fade({
  children,
  top,
  bottom,
  left,
  right,
  duration = 1000,
  distance = "50px",
}) {
  const ref = useRef(null);
  // hidden -> showing (transition en cours) -> done (plus de style imposé)
  const [phase, setPhase] = useState(() =>
    prefersReducedMotion() ? "done" : "hidden"
  );

  useEffect(() => {
    if (phase !== "hidden") return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const onScreen = () => {
      const r = el.getBoundingClientRect();
      const h = window.innerHeight || document.documentElement.clientHeight;
      return r.bottom > 0 && r.top < h;
    };

    if (!("IntersectionObserver" in window) || onScreen()) {
      setPhase("showing");
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        setPhase("showing");
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [phase]);

  useEffect(() => {
    if (phase !== "showing") return undefined;
    const timer = setTimeout(() => setPhase("done"), duration + 100);
    return () => clearTimeout(timer);
  }, [phase, duration]);

  const [dx, dy] = top
    ? DIRECTIONS.top
    : bottom
    ? DIRECTIONS.bottom
    : left
    ? DIRECTIONS.left
    : right
    ? DIRECTIONS.right
    : [0, 0];

  let revealStyle;
  if (phase === "hidden") {
    revealStyle = {
      opacity: 0,
      transform:
        dx || dy
          ? `translate3d(calc(${dx} * ${distance}), calc(${dy} * ${distance}), 0)`
          : "none",
    };
  } else if (phase === "showing") {
    revealStyle = {
      opacity: 1,
      transform: "none",
      transition: `opacity ${duration}ms ease-out, transform ${duration}ms cubic-bezier(0.2, 0.65, 0.3, 1)`,
    };
  }

  // Enfant DOM unique : on l'anime directement (pas de div en plus, la mise en
  // page reste celle de l'élément). Sinon on enveloppe dans une div.
  const only = React.Children.count(children) === 1 ? children : null;
  if (React.isValidElement(only) && typeof only.type === "string") {
    return React.cloneElement(only, {
      ref,
      style: revealStyle
        ? { ...only.props.style, ...revealStyle }
        : only.props.style,
    });
  }
  return (
    <div ref={ref} style={revealStyle}>
      {children}
    </div>
  );
}

export default Fade;
